import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { apiRequest, getImageUrl } from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar, { slicePage } from "../../../shared/PaginationBar";

type Category = {
  _id: string;
  name: string;
  slug?: string;
  description?: string;
  image?: string;
  isActive?: boolean;
};

type Product = {
  category?: string | { _id?: string; name?: string };
};

const CategoriesList = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const [categorySearch, setCategorySearch] = useState("");
  const [isActive, setIsActive] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const loadData = async () => {
    setLoading(true);

    try {
      const categoryData = await apiRequest("/categories", "GET");
      setCategories(categoryData.categories || []);

      try {
        const productData = await apiRequest("/products?limit=100", "GET");
        setProducts(productData.products || []);
      } catch {
        setProducts([]);
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const matchProduct = (reference: Product["category"], item: Category) =>
    reference === item._id ||
    reference === item.name ||
    (typeof reference === "object" &&
      (reference?._id === item._id || reference?.name === item.name));

  const productCount = (item: Category) =>
    products.filter((product) => matchProduct(product.category, item)).length;

  const filteredCategories = categories.filter((category) => {
    const searchMatch = category.name
      .toLowerCase()
      .includes(categorySearch.toLowerCase());

    const statusMatch =
      isActive === "all" ||
      (category.isActive ?? false) === (isActive === "true");

    return searchMatch && statusMatch;
  });

  useEffect(() => {
    setPage(1);
  }, [categorySearch, isActive]);

  const paged = slicePage(filteredCategories, page, limit);

  const deleteItem = async (id: string, name: string) => {
    const confirmation = await Swal.fire({
      title: `Delete ${name}?`,
      text: "Its subcategories will also be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (!confirmation.isConfirmed) return;

    try {
      const result = await apiRequest(`/categories/${id}`, "DELETE");

      if (!result.success) {
        throw new Error(result.message || "Delete failed");
      }

      await Swal.fire({
        title: "Deleted",
        text: result.message || "Deleted successfully",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });

      await loadData();
    } catch (error: any) {
      Swal.fire("Delete Failed", error.message, "error");
    }
  };

  const editItem = (id: string) => {
    const item = categories.find((category) => category._id === id);

    if (!item) return;

    navigate("/add-categories", {
      state: {
        editType: "category",
        item,
      },
    });
  };

  const metrics = [
    {
      label: "Total Categories",
      value: categories.length,
      icon: "🗂️",
      tone: "purple",
    },
    {
      label: "Active Categories",
      value: categories.filter((item) => item.isActive === true).length,
      icon: "✅",
      tone: "green",
    },
  ];

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Catalog</div>

          <h1 className="page-title">
            All <em>Categories</em>
          </h1>

          <div className="page-sub">Manage product categories and subcategories.</div>
        </div>

        <div className="page-hdr-actions">
          <NavLink to="/add-categories" className="btn-sm btn-primary">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add Category
          </NavLink>
        </div>
      </div>

      <section className="metrics-grid">
        {metrics.map((item) => (
          <MetricCard key={item.label} metric={item} />
        ))}
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>

          <input
            value={categorySearch}
            placeholder="Search categories..."
            onChange={(event) => setCategorySearch(event.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={isActive}
          onChange={(event) => setIsActive(event.target.value)}
        >
          <option value="all">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      <ListCard
        rows={paged.rows}
        loading={loading}
        productCount={productCount}
        onEdit={editItem}
        onDelete={deleteItem}
        page={paged.page}
        limit={limit}
        total={paged.total}
        totalPages={paged.totalPages}
        onPageChange={setPage}
        onLimitChange={(nextLimit) => {
          setPage(1);
          setLimit(nextLimit);
        }}
      />
    </>
  );
};

type ListCardProps = {
  rows: Category[];
  loading: boolean;
  productCount: (item: Category) => number;
  onEdit: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
};

const ListCard = ({
  rows,
  loading,
  productCount,
  onEdit,
  onDelete,
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  onLimitChange,
}: ListCardProps) => {
  return (
    <section className="table-card">
      <div className="table-head">
        <h2>Category List</h2>
        <p>Manage product categories</p>
      </div>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Slug</th>
              <th>Products</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="empty-state">
                  Loading categories...
                </td>
              </tr>
            ) : total === 0 ? (
              <tr>
                <td colSpan={5} className="empty-state">
                  No data found.
                </td>
              </tr>
            ) : (
              rows.map((item) => {
                return (
                  <tr key={item._id}>
                    <td>
                      <div className="img-name-cell">
                        <AppImage
                          src={getImageUrl(item.image)}
                          alt={item.name}
                          crossOrigin="anonymous"
                        />

                        <div>
                          <strong>{item.name}</strong>
                          <small className="table-muted">{item.description || "No description"}</small>
                        </div>
                      </div>
                    </td>

                    <td>{item.slug || "-"}</td>
                    <td>{productCount(item)}</td>

                    <td>
                      <span
                        className={`status-badge ${
                          item.isActive === true ? "success" : "danger"
                        }`}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td>
                      <div className="actions-btns order-actions">
                        <button
                          type="button"
                          className="action-button edit"
                          onClick={() => onEdit(item._id)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="action-button delete"
                          onClick={() => onDelete(item._id, item.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={page}
        limit={limit}
        total={total}
        totalPages={totalPages}
        itemLabel="categories"
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />
    </section>
  );
};

export default CategoriesList;
